/**
 * Core Web Vitals reporter.
 *
 * Subscribes to the four Google ranking signals (LCP, INP, CLS, plus FCP
 * and TTFB for diagnostics) and forwards them to:
 *   - window.gtag if Google Analytics is loaded
 *   - console.info in dev so we can spot regressions locally
 *
 * Sampling: GA's free tier handles bursts fine. We don't sample here.
 *
 * The library is dynamically imported so it doesn't sit in the critical
 * path of the initial JS bundle.
 */

type MetricLike = {
  name: string
  value: number
  id: string
  rating?: 'good' | 'needs-improvement' | 'poor'
  navigationType?: string
}

declare global {
  interface Window {
    gtag?: (
      command: 'event',
      action: string,
      params: Record<string, string | number | boolean | undefined>,
    ) => void
  }
}

function reportToGtag(metric: MetricLike) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  // event_category 'Web Vitals' groups them in GA reports. Value is in ms
  // for time-based metrics, unitless * 1000 for CLS so it survives GA's
  // integer event_value coercion.
  const isCLS = metric.name === 'CLS'
  window.gtag('event', metric.name, {
    event_category: 'Web Vitals',
    event_label: metric.id,
    value: Math.round(isCLS ? metric.value * 1000 : metric.value),
    metric_rating: metric.rating,
    metric_value: metric.value,
    non_interaction: true,
  })
}

function logToConsole(metric: MetricLike) {
  if (typeof window === 'undefined') return
  // Show in dev so we can see regressions during development. Suppressed
  // in prod by checking import.meta.env which Vite replaces at build time.
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info(
      `[web-vitals] ${metric.name}=${metric.value.toFixed(2)} (${metric.rating || 'n/a'})`,
    )
  }
}

export async function initWebVitals() {
  if (typeof window === 'undefined') return
  try {
    const { onCLS, onINP, onLCP, onFCP, onTTFB } = await import('web-vitals')
    const handler = (m: MetricLike) => {
      reportToGtag(m)
      logToConsole(m)
    }
    onCLS(handler)
    onINP(handler)
    onLCP(handler)
    onFCP(handler)
    onTTFB(handler)
  } catch (e) {
    // Don't break the page if the library fails to load
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('web-vitals failed to initialise:', e)
    }
  }
}
